import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

import useAnimate from '../ui/Motion';

import Reveal from '../ui/Reveal';
import useMediaQuery from '../ui/useMediaQuery';

const EASE = [0.16, 1, 0.3, 1];

/**
 * One card in the deck. The slot stays in normal flow so its scroll
 * progress is measurable; the inner shell is what actually sticks. As
 * the next card climbs over this one, the card underneath shrinks and
 * dims: the deck effect.
 *
 * `focus` is the old `horizontalPosition` from the Bootstrap build,
 * carried through as an object-position so each photograph still holds
 * the part of the frame it was cropped for.
 */
function StoryCard({ item, index, count, stacked }) {
    const slotRef = useRef(null);
    const reduced = !useAnimate();

    // The last card has no card climbing over it, so it never recedes.
    const isLast = index === count - 1;
    const animate = stacked && !reduced && !isLast;

    const { scrollYProgress } = useScroll({
        target: slotRef,
        offset: ['start start', 'end start']
    });

    // A slot spans every stride still to come, so the card directly above
    // this one finishes covering it after the first of those strides.
    const coverEnd = 1 / Math.max(1, count - 1 - index);

    const scale = useTransform(scrollYProgress, [0, coverEnd], [1, 0.9]);
    const dim = useTransform(scrollYProgress, [0, coverEnd], [0, 0.55]);

    const { scrollYProgress: passProgress } = useScroll({
        target: slotRef,
        offset: ['start end', 'end start']
    });

    const photoY = useTransform(passProgress, [0, 1], ['-9%', '9%']);

    return (
        <div className="deck__slot" ref={slotRef}>
            <div className="deck__sticky">
                <motion.article
                    className={`story${index % 2 === 1 ? ' story--flip' : ''}`}
                    id={item.id}
                    style={animate ? { scale } : undefined}
                >
                    <div className="story__media">
                        <motion.img
                            className="story__photo"
                            src={item.imgSrc}
                            alt={item.alt || ''}
                            loading={index === 0 ? undefined : 'lazy'}
                            decoding="async"
                            style={{
                                '--focus': `${item.focus || '50%'} center`,
                                ...(reduced ? {} : { y: photoY })
                            }}
                        />
                        <span className="story__media-veil" aria-hidden="true" />
                        <span className="story__index">{String(index + 1).padStart(2, '0')}</span>
                    </div>

                    <div className="story__body">
                        {item.kicker && (
                            <Reveal delay={0.04} y={18} amount={0.2}>
                                <span className="story__kicker">{item.kicker}</span>
                            </Reveal>
                        )}

                        <Reveal delay={0.08} y={20} amount={0.2}>
                            <h3 className="story__title">{item.title}</h3>
                        </Reveal>

                        <Reveal delay={0.14} y={20} amount={0.2}>
                            <p className="story__copy">{item.text}</p>
                        </Reveal>

                        {item.points && item.points.length > 0 && (
                            <>
                                <hr className="rule story__rule" />
                                <ul className="story__points">
                                    {item.points.map((point, i) => (
                                        <motion.li
                                            key={point}
                                            initial={reduced ? false : { opacity: 0, y: 10 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, amount: 0.4 }}
                                            transition={{ duration: 0.5, delay: 0.03 * i, ease: EASE }}
                                        >
                                            {point}
                                        </motion.li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    {animate && (
                        <motion.span
                            className="story__dim"
                            style={{ opacity: dim }}
                            aria-hidden="true"
                        />
                    )}
                </motion.article>
            </div>
        </div>
    );
}

/**
 * The deck. Sticky stacking is a desktop behaviour only: on a phone a
 * pinned stack fights the collapsing browser chrome, and there is not
 * enough viewport left for a card to be legible under the nav.
 */
export default function StoryDeck({ items }) {
    const stacked = useMediaQuery('(min-width: 981px)');

    return (
        <div className={`shell deck${stacked ? ' deck--stacked' : ''}`}>
            {items.map((item, index) => (
                <StoryCard
                    key={item.title}
                    item={item}
                    index={index}
                    count={items.length}
                    stacked={stacked}
                />
            ))}
        </div>
    );
}
